import { Location } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { serverTimestamp, Timestamp } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
// import moment from 'moment';
import dayjs from 'dayjs';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  EMPTY,
  from,
  mergeMap,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from 'rxjs';
import { FireTimestamp } from '../..//models/timestamp.firestore.model';
import { UserModel } from '../..//models/user.model';
import { Event, EventStatus } from '../../models/event.model';
import { EventService } from '../../services/event.service';
import * as eventActions from '../actions/event.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';

@Injectable()
export class EventEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  location = inject(Location);
  eventService: EventService = inject(EventService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);

  user: UserModel | null = null;
  userSub: Subscription | undefined;

  private logout$ = new Subject<void>();

  constructor() {
    this.userSub = this.store
      .select((state) => state.user.user)
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
  }

  // Effect to fetch group Events
  getEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(eventActions.getEvents),
      switchMap(({ groupId, startDate, endDate, lastRecord }) => {
        if (!this.user?.uid) return EMPTY;
        this.logout$.next();
        var query: AngularFirestoreCollection<Event> =
          this.afs.collection<Event>('events', (ref) => {
            var reference = ref
              .where('groupId', '==', groupId)
              .where(
                'startDateTime',
                '>=',
                startDate ??
                  Timestamp.fromDate(dayjs().startOf('month').toDate())
              )
              .where(
                'endDateTime',
                '<=',
                endDate ?? Timestamp.fromDate(dayjs().endOf('month').toDate())
              )
              .where('status', 'in', [
                EventStatus.active,
                EventStatus.cancelled,
              ])
              .limit(100)
              .orderBy('startDateTime', 'desc');
            if (lastRecord) reference = reference.startAfter(lastRecord);
            return reference;
          });
        return query.get().pipe(takeUntil(this.logout$));
      }),
      switchMap((events) => {
        let eventsList: Event[] = events.docs.map((doc) => {
          const event = doc.data() as Event;
          event.id = doc.id;
          return event;
        });
        return of(
          eventActions.loadEvents({
            events: eventsList,
          })
        );
      }),
      catchError((error) => {
        this.toastr.error(
          'An error occurred. Please try again later.',
          'Error Fetching Events'
        );
        return of(eventActions.getEventsFailure({ error }));
      })
    )
  );

  // Fetch recently created Event
  getEvent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(eventActions.createSuccess),
      switchMap(({ event }) => {
        return this.afs.collection<Event>('events').doc(event.id).get();
      }),
      switchMap((snapshot) => {
        const event = snapshot.data() as Event;
        event.id = snapshot.id;
        return of(
          eventActions.loadEvent({
            event: event,
          })
        );
      })
    )
  );

  // Create Event
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(eventActions.create),
      mergeMap(async ({ id, event }) => {
        if (this.user == null) {
          this.toastr.error(
            'User not found. Please try again later',
            'Error Creating Event'
          );
          return eventActions.createFailure({ error: 'User not found' });
        }

        let e: any = {
          ...event,
          createdBy: this.user!.name,
          createdById: this.user!.uid,
          requestedBy: this.user!.name,
          requestedById: this.user!.uid,
          approvedBy: this.user!.name,
          approvedById: this.user!.uid,
          created: serverTimestamp(),
          modified: serverTimestamp(),
        };

        return await this.eventService
          .create(id, e)
          .then(() => {
            this.toastr.success(
              'The Event has been created successfully.',
              'Event Created Successfully'
            );
            this.router.navigate(['groups', event.groupId, 'events']);
            // this.location.back();
            e.created = FireTimestamp.fromDate(dayjs().toDate());
            e.modified = FireTimestamp.fromDate(dayjs().toDate());
            e.id = id;
            // console.log(`create$: ${JSON.stringify(e, null, 2)}`);
            return eventActions.createSuccess({ event: e as Event });
          })
          .catch((error) => {
            console.error(`create$: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Creation Failed'
            );
            return eventActions.createFailure({ error });
          });
      })
    )
  );

  // Update Event
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(eventActions.update),
      switchMap(async ({ id, event }) => {
        if (this.user == null) {
          this.toastr.error(
            'User not found. Please try again later',
            'Error Updating Event'
          );
          return eventActions.updateFailure({ error: 'User not found' });
        }

        let e: Event = {
          ...event,
          approvedBy: this.user!.name,
          approvedById: this.user!.uid,
          modified: serverTimestamp() as Timestamp,
        };

        return await this.eventService
          .update(id, e)
          .then(() => {
            this.toastr.success(
              'The Event has been updated successfully.',
              'Event Updated Successfully'
            );
            // this.router.navigate(['/groups/' + event.groupId + '/events']);
            this.location.back();
            e.modified = event.modified;
            // console.log(`update$: ${JSON.stringify(e, null, 2)}`);
            return eventActions.updateSuccess({
              event: { ...e, id: id } as Event,
            });
          })
          .catch((error) => {
            console.error(`update$: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Updating Event'
            );
            return eventActions.updateFailure({ error });
          });
      })
    )
  );

  // Effect to update a prayer status
  updateStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(eventActions.updateStatus),
      switchMap(async ({ id, event }) => {
        const _ = await this.eventService.update(id, {
          ...event,
          approvedBy: this.user?.name,
          approvedById: this.user?.uid,
          modified: serverTimestamp() as Timestamp,
        });
        const localPrayer: Event = {
          ...event,
          id: id,
          modified: event.modified,
        };
        return eventActions.updateStatusSuccess({ event: localPrayer });
      }),
      catchError((error) => {
        // console.error(`Error updating event: ${error}`);
        this.toastr.error(
          `An error occurred, '${error}'. Please try again later.`,
          'Error Updating Event Status'
        );
        return of(eventActions.updateStatusFailure({ error }));
      })
    )
  );

  // effect to delete a event
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(eventActions.deleteAction),
      switchMap(({ id }) =>
        this.eventService
          .delete(id)
          .then(() => {
            this.toastr.success(
              'The event has been deleted successfully.',
              'Event Deleted'
            );
            this.router.navigate(['..']);
            return eventActions.deleteSuccess({ id: id });
          })
          .catch((error) => {
            // console.error(`Error deleting event: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Event'
            );
            return eventActions.deleteFailure({ error });
          })
      )
    )
  );

  // Get Event Statistics
  getEventStatistics$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        eventActions.getEventStats,
        eventActions.createSuccess,
        eventActions.updateStatusSuccess
      ),
      switchMap((action) => {
        let groupId = '';
        if (action.type === eventActions.getEventStats.type) {
          groupId = action.groupId;
        } else if (action.type === eventActions.createSuccess.type) {
          groupId = action.event.groupId;
        } else if (action.type === eventActions.updateStatusSuccess.type) {
          groupId = action.event.groupId;
        }

        return from(this.eventService.getEventStats(groupId)).pipe(
          switchMap((snapshot) => {
            let g: any = {
              id: groupId,
              activeEvents: snapshot?.upcoming ?? '-',
              completedEvents: snapshot?.completed ?? '-',
            };
            return of(eventActions.getEventStatsDone({ group: g }));
          }),
          catchError((error) =>
            of(eventActions.getEventStatsFailure({ error }))
          )
        );
      })
    )
  );

  clearEvents$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(eventActions.clear());
      })
    )
  );
}
